// ============================================================
// TMR Portal Admin — Parties reads and writes, over @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import type { Paged } from '@tmr/api-client';
import { partyKeys } from '../queryClient';
import { exportPageReader } from '../../lib/export/exportPageReader';
import type {
  CreatePartyRequest,
  CreatePartyResult,
  PartyListItem,
  PartyOption,
  PartyRecord,
  UpdatePartyRequest,
} from './contract';
import { withActive } from './form';

/** Filters the Partes list accepts; they are part of the cache key, so a change refetches. */
export interface PartyListFilters {
  /** Free text matched by the server against name, CPF and e-mail. */
  search?: string;
  /** True lists only active people, false only archived ones, omitted both. */
  active?: boolean;
  page?: number;
  pageSize?: number;
}

/** How many people the table shows per page. */
export const PARTY_PAGE_SIZE = 10;

/** How many people a picker shows — enough to choose from, never a report. */
const PICKER_PAGE_SIZE = 8;

/** Below this many characters a search is not worth a round-trip, so the picker stays idle. */
const MIN_SEARCH_LENGTH = 2;

/**
 * One page of the Parties directory, already scoped by the backend (06 §6).
 *
 * A person is visible when they are attached to a Company in the operator's scope; an unscoped operator
 * sees everybody, including the people no cadastre has linked yet. Gated by `parties.read` on the server,
 * so the screen checks the permission instead of letting every page 403 (skill `frontend-architecture`,
 * Regra 5).
 */
export function usePartyList(filters: PartyListFilters = {}): UseQueryResult<Paged<PartyListItem>> {
  const query = {
    search: filters.search,
    active: filters.active,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? PARTY_PAGE_SIZE,
  };

  return useQuery({
    queryKey: partyKeys.list(query),
    queryFn: ({ signal }) => api.get<Paged<PartyListItem>>('/parties', { query, signal }),
    // Keeps the current page on screen while the next loads, so the table does not collapse into a
    // skeleton on every keystroke of the search box.
    placeholderData: (previous) => previous,
  });
}

/**
 * Reads one page of the Parties **export** (20 §3) — the same filters the table is using.
 *
 * @param filters The screen's active filters.
 */
export function partyExportPage(filters: PartyListFilters = {}) {
  return exportPageReader<PartyListItem>('/parties/export', {
    search: filters.search,
    active: filters.active,
  });
}

/**
 * One person's full record (`GET /parties/{id}`) — the note and the associations the list does not carry.
 *
 * Every write starts here: `PUT /parties/{id}` replaces the whole record, so editing, archiving and
 * linking all need what the person already has. An id outside the operator's scope answers 404 exactly
 * like an unknown one (06 §6), which lands on the same "não encontrada" state.
 *
 * @param partyId The person to read, or null to stay idle.
 */
export function useParty(partyId: string | null): UseQueryResult<PartyRecord> {
  return useQuery({
    queryKey: partyKeys.detail(partyId ?? ''),
    queryFn: ({ signal }) => api.get<PartyRecord>(`/parties/${partyId}`, { signal }),
    enabled: Boolean(partyId),
  });
}

/**
 * People matching a term, for the Empresa cadastre's contact blocks (10 B5b).
 *
 * Only active people are offered — an archived Party is off the pickers by design, which is the whole
 * point of archiving instead of deleting. Gated by `parties.read` on the server; the caller degrades the
 * field to a plain input without it.
 *
 * @param term Free text matched by the server against name, CPF and e-mail.
 */
export function usePartySearch(term: string): UseQueryResult<PartyOption[]> {
  const trimmed = term.trim();
  const query = { search: trimmed, active: true, page: 1, pageSize: PICKER_PAGE_SIZE };

  return useQuery({
    queryKey: partyKeys.search(query),
    queryFn: ({ signal }) => api.get<Paged<PartyOption>>('/parties', { query, signal }),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    select: (page) => page.items,
  });
}

/**
 * Registers a person (`POST /parties`). Gated by `parties.manage` on the server.
 *
 * A duplicate CPF comes back as a 409 the caller shows inline rather than merging two people — two
 * records with the same CPF is a question for staff, not something a client should resolve by guessing.
 */
export function useCreateParty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreatePartyRequest) => api.post<CreatePartyResult>('/parties', request),
    onSuccess: () => {
      // The new person belongs on every list and in every picker, whatever filter is showing.
      void queryClient.invalidateQueries({ queryKey: partyKeys.all });
    },
  });
}

/**
 * Corrects a person (`PUT /parties/{id}`) — details, associations and the archive flag at once.
 *
 * The request replaces the record, so callers build it with `toUpdateRequest` from a record they read
 * first. Passing a payload assembled from a list row would wipe the note and every association.
 */
export function useUpdateParty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...request }: UpdatePartyRequest & { id: string }) =>
      api.put<void>(`/parties/${id}`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partyKeys.all });
    },
  });
}

/**
 * Archives or restores a person from a list row, without disturbing anything else about them.
 *
 * There is no archive endpoint — `PUT` is the only writer — so this reads the record first and sends it
 * back with `active` flipped. That round-trip is the point: a list row does not carry the note or the
 * associations, and a payload built from one would archive the person by deleting half their record.
 */
export function useSetPartyActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const record = await fetchParty(queryClient, id);
      await api.put<void>(`/parties/${id}`, withActive(record, active));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partyKeys.all });
    },
  });
}

/** Reads a record through the cache, so an edit that just loaded it does not fetch it twice. */
function fetchParty(queryClient: QueryClient, partyId: string): Promise<PartyRecord> {
  return queryClient.fetchQuery({
    queryKey: partyKeys.detail(partyId),
    queryFn: ({ signal }) => api.get<PartyRecord>(`/parties/${partyId}`, { signal }),
  });
}
